import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { Check, Construction } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import { productsContent } from '../content/pages/products';

interface Product {
  id: string;
  name: string;
  tagline: string;
  image: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  status?: string;
}

const ProductCard: React.FC<{ product: Product }> = ({ product }) => (
  <Card sx={{
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    borderRadius: 2,
    boxShadow: 3,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 6
    }
  }}>
    <CardMedia
      component="img"
      image={product.image}
      alt={product.name}
      sx={{
        width: { xs: '100%', md: 300 },
        height: { xs: 200, md: 'auto' },
        minHeight: { md: 250 },
        objectFit: 'cover',
        flexShrink: 0
      }}
    />
    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
          {product.name}
        </Typography>
        {product.status && (
          <Chip
            icon={<Construction sx={{ fontSize: 16 }} />}
            label={product.status}
            size="small"
            sx={{
              bgcolor: '#ff9800',
              color: 'white',
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'white' }
            }}
          />
        )}
      </Box>
      <Typography variant="subtitle1" sx={{ color: 'primary.main', mb: 2, fontWeight: 500 }}>
        {product.tagline}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
        {product.description}
      </Typography>

      <Box sx={{ flexGrow: 1 }}>
        {product.features.map((feature, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Check sx={{ fontSize: 18, color: 'success.main', mr: 1, mt: 0.3 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        component={Link}
        to={product.ctaLink}
        variant="outlined"
        fullWidth
        sx={{
          mt: 3,
          borderColor: 'primary.main',
          color: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.main',
            color: 'white'
          }
        }}
      >
        {product.cta}
      </Button>
    </CardContent>
  </Card>
);

const Products: React.FC = () => {
  const c = productsContent;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
              {c.hero.subtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Display Options Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {c.displayOptions.title}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7, maxWidth: 700, mx: 'auto' }}>
            {c.displayOptions.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {c.displayOptions.products.map((product) => (
            <Grid item xs={12} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Comparison Table */}
      <Box sx={{ bgcolor: '#f9f9f9', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4, color: 'primary.main' }}>
            {c.comparison.title}
          </Typography>

          {/* Desktop Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {c.comparison.headers.map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        textAlign: idx === 0 ? 'left' : 'center'
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {c.comparison.rows.map((row, idx) => (
                  <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{row.feature}</TableCell>
                    {row.values.map((value, vIdx) => (
                      <TableCell key={vIdx} sx={{ textAlign: 'center' }}>
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Cards */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Grid container spacing={2}>
              {c.comparison.headers.slice(1).map((productName, productIdx) => (
                <Grid item xs={12} key={productIdx}>
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 2, py: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {productName}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {c.comparison.rows.map((row, rowIdx) => (
                        <Box
                          key={rowIdx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: rowIdx < c.comparison.rows.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {row.feature}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.values[productIdx]}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Add-Ons Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {c.addOns.title}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary', lineHeight: 1.7, maxWidth: 700, mx: 'auto' }}>
            {c.addOns.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {c.addOns.products.map((product) => (
            <Grid item xs={12} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Bottom CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {c.cta.title}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', opacity: 0.9, mb: 4, lineHeight: 1.7 }}>
            {c.cta.description}
          </Typography>
          <Button
            component={Link}
            to={c.cta.buttonLink}
            variant="outlined"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              color: 'white',
              borderColor: 'white',
              borderWidth: 2,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
                borderColor: 'white',
                borderWidth: 2
              }
            }}
          >
            {c.cta.buttonText}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Products;
